import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FETCH_SIZE_CARD_DATA } from "../../DataTagConstants";
import { Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import "./DataEstateSizeCard.scss";
import { percentageCalculation } from "../../../../components/common/utilities";
import { filesize } from "filesize";
import { numberFormatter } from "../../../../components/common/Dashboard/utils";

const formatSize = (bytes) => {
  return filesize(bytes, { standard: "jedec" });
};

const calcPercent = (value, total) =>
  total > 0 ? ((value / total) * 100).toFixed(1) : 0;

const getOrCreateTooltipEl = (wrapper) => {
  let el = wrapper.querySelector(".de-chart-tooltip");
  if (!el) {
    el = document.createElement("div");
    el.className = "de-chart-tooltip";
    wrapper.appendChild(el);
  }
  return el;
};

const externalTooltipHandler = (context) => {
  const { chart, tooltip } = context;
  const wrapper = chart.canvas.closest(".data-estate-chart");
  const tooltipEl = getOrCreateTooltipEl(wrapper);

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  if (tooltip.body) {
    const lines = tooltip.body.map((b) => b.lines).flat();
    tooltipEl.innerHTML = lines.map((line) => `<div>${line}</div>`).join("");
  }

  const { offsetLeft: canvasX, offsetTop: canvasY } = chart.canvas;

  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = `${canvasX + tooltip.caretX}px`;
  tooltipEl.style.top = `${canvasY + tooltip.caretY}px`;
};

function DataEstateSizeCard() {
  const sizeData = useSelector((state) => state.dataTagReducer.sizeCardData);
  const dataSourceType = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.type,
  );
  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id,
  );
  const isDataBase = useSelector((state) => state.dataTagReducer.isDatabase);


  const dispatch = useDispatch();

  useEffect(() => {
    if (dataSourceId) {
      dispatch({ type: FETCH_SIZE_CARD_DATA, payload: dataSourceId });
    }
  }, [dataSourceId]);

  if (!sizeData) return <div>Loading...</div>;

  const sizeMB = sizeData?.size?.[0]?.["DataTagSensitivity.fileSizeTotal"] || 0;

  // pdaCount — currently correct (already updated to sensitiveData)
  const pdaCount = isDataBase
    ? sizeData?.totalColumns?.[0]?.["DataguardDbViewTotal.totalColumns"] || 0
    : sizeData?.sensitiveData?.[0]?.["DataTagSensitivity.publicFilesCount"] ||
      0;

  const nonPdaCount =
    Number(
      sizeData?.sensitiveData?.[0]?.[
        "DataTagSensitivity.publicAccessCheckedCount"
      ] || 0,
    ) -
    Number(
      sizeData?.sensitiveData?.[0]?.["DataTagSensitivity.publicFilesCount"] ||
        0,
    );

  const pdaRemainingCount = Number(
    sizeData?.sensitiveData?.[0]?.["DataTagSensitivity.notScannedCount"] || 0,
  );

  const regulatedCount = isDataBase
  ? sizeData?.regulatedTables?.[0]?.["DataguardDbViewTotal.regulatedTables"] || 0
  : sizeData?.regulatedFileCount?.[0]?.["FileClassificationStats.fileCount"] || 0;

  const sensitiveCount = isDataBase
    ? sizeData?.sensitiveColumns?.[0]?.[
        "DataguardDbViewTotal.sensitiveColumns"
      ] || 0
    : sizeData?.processed?.[0]?.["DataTagSensitivity.processedCount"] || 0;

  const totalCount = isDataBase
    ? sizeData?.totalColumns?.[0]?.["DataguardDbViewTotal.totalColumns"] || 0
    : sizeData?.total?.[0]?.["DataTagSensitivity.count"] || 0;

  const totalTablesCount =
    sizeData?.totalTables?.[0]?.["DataguardDbViewTotal.totalTables"] || 0;

  // ---------------------------------------------------------------------------
  // PDA BREAKDOWN — non-database only. "pdaCount" above already IS the
  // checked/processed count (DataTagPDA.publicAccessCheckedCount), reused
  // as-is so nothing that already depends on it (the % bubble) changes.
  // These two are new, purely for the 3-line PDA tooltip.
  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  // CENTER PIE — Processed vs Not Processed. Untouched isDataBase branching:
  // the saga already reshapes both branches into these same two keys, so
  // this reads identically regardless of source type.
  // ---------------------------------------------------------------------------

  const processedCount = Number(
    sizeData?.processed?.[0]?.["DataTagSensitivity.processedCount"] || 0,
  );

  const notProcessedCount = Number(
    sizeData?.notProcessed?.[0]?.["DataTagSensitivity.notProcessedCount"] || 0,
  );

  const formattedSize = formatSize(sizeMB);

  const isNTFS = dataSourceType === "NTFS";

  const pdaPercent =
    percentageCalculation([{ count: pdaCount }], totalCount)[0]?.count || 0;
  const regulatedPercent =
    percentageCalculation([{ count: regulatedCount }], totalCount)[0]?.count ||
    0;
  const sensitivePercent =
    percentageCalculation([{ count: sensitiveCount }], totalCount)[0]?.count ||
    0;

  const doughnutData = {
    datasets: [
      {
        labels: ["Regulated", "Remaining"],
        data: [regulatedPercent, 100 - regulatedPercent],
        backgroundColor: ["#ffa500", "#e5e5e5"],
        weight: 1,
        spacing: 4,
      },
      {
        labels: ["PDA", "Remaining"],
        data: [pdaPercent, 100 - pdaPercent],
        backgroundColor: ["#00aaff", "#e5e5e5"],
        weight: 1,
        spacing: 4,
      },
      {
        labels: ["Sensitive", "Remaining"],
        backgroundColor: ["#ff3366", "#e5e5e5"],
        data: [sensitivePercent, 100 - sensitivePercent],
        weight: 1,
        spacing: 4,
      },
    ],
  };

  // ---------------------------------------------------------------------------
  // CENTER FILLED PIE — cutout: "0%" makes this a solid pie, not a ring.
  // ---------------------------------------------------------------------------

  const processingPieData = {
    labels: ["Processed", "Not Processed"],
    datasets: [
      {
        data: [processedCount, notProcessedCount],
        backgroundColor: ["#4caf50", "#e5e5e5"],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  return (
    <div className="size-card">
      <div className="card-header size">
        <div>{isDataBase ? "Schema" : "Size"}</div>
        {!isDataBase && <div className="size-display">{formattedSize}</div>}
      </div>

      <div className="sizeCardLowerContainer">
        <div className="details">
          <div className="row">
            <div className="circle total"></div>
            <div>
              <span>{isDataBase ? "Total Tables" : "Total"}</span>
              <strong>
                {isDataBase
                  ? numberFormatter(
                      Number(totalTablesCount || 0).toLocaleString(),
                    )
                  : numberFormatter(totalCount)}
              </strong>
            </div>
          </div>

          <div className="row">
            <div className="circle regulated"></div>
            <div>
              <span>Regulated</span>
              <strong>{regulatedPercent}%</strong>
            </div>
          </div>

          <div className="row">
            <div className="circle pda"></div>
            <div>
              <span>{isDataBase ? "Total Columns" : "Public"}</span>
              <strong>
                {isDataBase ? numberFormatter(pdaCount) : isNTFS ? "N/A" : `${pdaPercent}%`}
              </strong>
            </div>
          </div>

          <div className="row">
            <span className="circle sensitivity"></span>
            <div>
              <span>Sensitivity</span>
              <strong>{sensitivePercent}%</strong>
            </div>
          </div>
        </div>

        <div className="chart data-estate-chart">
          {/* ============================================================= */}
          {/* OUTER THREE RINGS */}
          {/* ============================================================= */}

          <Doughnut
            data={doughnutData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              cutout: "42%",
              plugins: {
                legend: { display: false },
                datalabels: { display: false },
                tooltip: {
                  enabled: false, // stop drawing on the canvas
                  external: externalTooltipHandler,
                  callbacks: {
                    label: (context) => {
                      const label =
                        context.dataset.labels?.[context.dataIndex] || "";

                      const datasetIndex = context.datasetIndex;

                      // ---------------------------------------------------
                      // PDA ring — non-database gets a 3-line breakdown
                      // (Processed / Remaining / Public files found).
                      // Database keeps the original single-value behavior,
                      // exactly as before, so isDataBase logic is untouched.
                      // ---------------------------------------------------

                      if (datasetIndex === 1) {
                        if (isDataBase) {
                          if (label === "PDA") {
                            return `Total Columns: ${numberFormatter(pdaCount)}`;
                          }
                          return `Remaining: ${numberFormatter(
                            Math.max(0, totalCount - pdaCount),
                          )}`;
                        }

                        return [
                          `Public files: ${numberFormatter(pdaCount)}`,
                          `Non Public Files: ${numberFormatter(nonPdaCount)}`,
                          `Remaining Files: ${numberFormatter(pdaRemainingCount)}`,
                        ];
                      }

                      let value = 0;

                      if (datasetIndex === 0) {
                        if (label === "Regulated") value = regulatedCount;
                        if (label === "Remaining")
                          value = totalCount - regulatedCount;
                      }

                      if (datasetIndex === 2) {
                        if (label === "Sensitive") value = sensitiveCount;
                        if (label === "Remaining")
                          value = totalCount - sensitiveCount;
                      }

                      return `${label}: ${numberFormatter(value)}`;
                    },
                  },
                },
              },

              interaction: {
                mode: "nearest",
                intersect: true,
              },
            }}
          />

          {/* ============================================================= */}
          {/* CENTER FILLED PIE — Processed vs Not Processed, hover-only */}
          {/* ============================================================= */}

          <div className="processing-chart">
            <Doughnut
              data={processingPieData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: "0%",
                plugins: {
                  legend: { display: false },
                  datalabels: { display: false },
                  tooltip: {
                    enabled: false,
                    external: externalTooltipHandler,
                    callbacks: {
                      label: (context) => {
                        const label = context.label;
                        const value =
                          label === "Processed"
                            ? processedCount
                            : notProcessedCount;
                        return `${label}: ${numberFormatter(value)}`;
                      },
                    },
                  },
                },
                interaction: {
                  mode: "nearest",
                  intersect: true,
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataEstateSizeCard;
